import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAnalyticsConsent } from "../../hooks/useAnalyticsConsent";
import { useAuth } from "../../hooks/useAuth";
import { openCookiePreferences } from "./cookiePreferences";

/**
 * Soft, non-blocking analytics notice. Landing analytics run by default under
 * the first-party audience-measurement basis; this notice informs the visitor
 * and gives a one-click way to manage/opt out. Shown to a resolved guest until
 * they acknowledge ("Got it") or make a choice. Does NOT gate tracking.
 */
export function ConsentBanner() {
  const { t } = useTranslation();
  const { consent, grant } = useAnalyticsConsent();
  const { isAuthenticated, isLoading } = useAuth();

  // Only surface for a resolved guest who has not yet acknowledged/chosen.
  if (isLoading || isAuthenticated || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label={t("landing.consent.aria")}
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-xl ring-1 ring-black/5 backdrop-blur dark:border-white/10 dark:bg-slate-900/95 dark:ring-white/5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          {t("landing.consent.message")}{" "}
          <Link
            to="/cookies"
            className="font-medium text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
          >
            {t("landing.consent.learnMore")}
          </Link>
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={openCookiePreferences}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800 sm:flex-none"
          >
            {t("landing.consent.manage")}
          </button>
          <button
            type="button"
            onClick={grant}
            className="flex-1 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 sm:flex-none"
          >
            {t("landing.consent.gotIt")}
          </button>
        </div>
      </div>
    </div>
  );
}
